import React, { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { TMGColors } from "../../colors";
import styles from "./MGCard.module.css";

export const MGCardSpacing = {
  none: "0px",
  micro: "8px",
  mini: "12px",
  xxxs: "16px",
  xxs: "20px",
  xs: "24px",
  sm: "32px",
  md: "40px",
  lg: "48px",
  xl: "56px",
  xxl: "64px",
} as const;

export const MGCardBorderRadius = {
  micro: "8px",
  mini: "12px",
  xxxs: "16px",
  xxs: "20px",
  xs: "24px",
  sm: "32px",
  md: "40px",
  lg: "48px",
} as const;

export const MGCardShadow = {
  "0": "0 0 0",
  "10": "0 2px 6px",
  "20": "0 6px 10px",
  "30": "0 10px 14px",
  "40": "0 14px 18px",
  "50": "0 18px 22px",
  "60": "0 22px 26px",
  "70": "0 -2px 6px",
} as const;

export type TMGCardSpacing = keyof typeof MGCardSpacing;
export type TMGCardBorderRadius = keyof typeof MGCardBorderRadius;
export type TMGCardShadow = keyof typeof MGCardShadow;

export type TMGCardProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  backgroundColor?: TMGColors;
  backgroundImage?: string;
  borderRadius?: TMGCardBorderRadius;
  children?: ReactNode;
  dataTestId?: string;
  hasBorder?: boolean;
  /** @deprecated Prefer the individual padding properties. */
  padding?: TMGCardSpacing;
  paddingBottom?: TMGCardSpacing;
  paddingLeft?: TMGCardSpacing;
  paddingRight?: TMGCardSpacing;
  paddingTop?: TMGCardSpacing;
  shadow?: TMGCardShadow;
};

type TMGCardStyle = CSSProperties & {
  "--mg-card-background"?: string;
  "--mg-card-border-radius": string;
  "--mg-card-padding-bottom": string;
  "--mg-card-padding-left": string;
  "--mg-card-padding-right": string;
  "--mg-card-padding-top": string;
  "--mg-card-shadow": string;
};

export const MGCard = ({
  backgroundColor = "white",
  backgroundImage,
  borderRadius = "xxxs",
  children,
  className,
  dataTestId,
  hasBorder = false,
  padding,
  paddingBottom = padding ?? "xs",
  paddingLeft = padding ?? "xs",
  paddingRight = padding ?? "xs",
  paddingTop = padding ?? "xs",
  shadow = "0",
  style,
  ...htmlProps
}: TMGCardProps) => {
  const cardStyle: TMGCardStyle = {
    "--mg-card-background": backgroundColor ? `var(--mg-color-${backgroundColor})` : undefined,
    "--mg-card-border-radius": MGCardBorderRadius[borderRadius],
    "--mg-card-padding-bottom": MGCardSpacing[paddingBottom],
    "--mg-card-padding-left": MGCardSpacing[paddingLeft],
    "--mg-card-padding-right": MGCardSpacing[paddingRight],
    "--mg-card-padding-top": MGCardSpacing[paddingTop],
    "--mg-card-shadow": MGCardShadow[shadow],
    backgroundImage: backgroundImage ? `url(${JSON.stringify(backgroundImage)})` : undefined,
    ...style,
  };
  const showBorder = hasBorder || (!backgroundColor && !backgroundImage);

  return (
    <div
      {...htmlProps}
      className={`${styles.card}${className ? ` ${className}` : ""}`}
      data-has-border={showBorder || undefined}
      data-testid={dataTestId}
      style={cardStyle}>
      {children}
    </div>
  );
};
